import { useState } from 'react';
import Button from '@/components/Button';
import CurrencySelectBox from '@/components/CurrencySelectBox';
import PhoneNumberInput from '@/components/PhoneNumberInput';
import RetailerSelectBox from '@/components/RetailerSelectBox';
import ToggleSwitch from '@/components/ToggleSwitch';
import { NullIsland } from '@/models/Geolocation';
import { OrderPredictionAlgorithm } from '@/models/OrderPrediction';
import { isStatusActive } from '@/models/Status';
import { Store, StoreAddressType, StoreCreate } from '@/models/Store';
import OrderPredictionAlgorithmSelectBox from '@/modules/admin/components/OrderPredictionAlgorithmSelectBox';
import StoreAddressForm from '@/modules/admin/components/StoreAddressForm';
import { CheckIcon } from '@heroicons/react/24/solid';
import { Controller, FormProvider, useForm } from 'react-hook-form';

interface StoreFormProps {
  store?: Store | null;
  retailerId?: string;
  activeAddressType?: StoreAddressType.BUSINESS | StoreAddressType.DELIVERY;
  mode?: 'full' | 'details' | 'address';
  onSubmit: (store: StoreCreate | Store) => Promise<void>;
}

const getAddressLabel = (type: StoreAddressType.BUSINESS | StoreAddressType.DELIVERY) =>
  type === StoreAddressType.DELIVERY ? 'Delivery Address' : 'Business Address';

const inputClassName =
  'mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/10';

const textAreaClassName = `${inputClassName} min-h-[120px] resize-none`;

const sectionClassName = 'rounded-2xl border border-slate-200 bg-white p-6 lg:p-8';

const StoreForm = ({
  store,
  onSubmit,
  retailerId,
  activeAddressType = StoreAddressType.BUSINESS,
  mode = 'full',
}: StoreFormProps) => {
  const [loading, setLoading] = useState(false);

  const defaultBusinessAddress = {
    storeId: '',
    type: StoreAddressType.BUSINESS,
    building: '',
    street1: '',
    street2: '',
    postalCode: '',
    region: '',
    city: '',
    countryCode: '',
    location: { ...NullIsland },
    additionalDescription: '',
  };

  const defaultDeliveryAddress = {
    storeId: '',
    type: StoreAddressType.DELIVERY,
    building: '',
    street1: '',
    street2: '',
    postalCode: '',
    region: '',
    city: '',
    countryCode: '',
    location: { ...NullIsland },
    additionalDescription: '',
  };

  const normalizeAddresses = (addresses: Store['addresses'] = []) => {
    const businessAddress = addresses.find((a) => a.type === StoreAddressType.BUSINESS) || {
      ...defaultBusinessAddress,
    };
    const deliveryAddress = addresses.find((a) => a.type === StoreAddressType.DELIVERY) || {
      ...defaultDeliveryAddress,
    };
    return [businessAddress, deliveryAddress];
  };

  const methods = useForm<StoreCreate | Store>({
    defaultValues: store
      ? {
          ...store,
          addresses: normalizeAddresses(store.addresses),
        }
      : {
          code: '',
          retailerId: retailerId || '',
          name: '',
          email: '',
          description: '',
          externalCode: '',
          contactCountryCode: '',
          contactNumber: '',
          currencyCode: '',
          addresses: normalizeAddresses(),
          receivingWindows: [],
          customData: {},
          dynamicFields: {},
          showVat: false,
          orderPrediction: {
            enabled: false,
            algorithm: 'simple_reorder',
          },
          status: 'ACTIVE',
        },
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  const status = watch('status');
  const showVat = watch('showVat');
  const orderPredictionEnabled = watch('orderPrediction.enabled');
  const defaultAlgorithm: OrderPredictionAlgorithm = 'simple_reorder';
  const orderPredictionAlgorithm = watch('orderPrediction.algorithm') || defaultAlgorithm;
  const contactCountryCode = watch('contactCountryCode');
  const addresses = watch('addresses');
  const businessAddressIndex = addresses.findIndex(
    (address) => address.type === StoreAddressType.BUSINESS,
  );
  const deliveryAddressIndex = addresses.findIndex(
    (address) => address.type === StoreAddressType.DELIVERY,
  );
  const activeAddressIndex = addresses.findIndex((address) => address.type === activeAddressType);

  const handleRetailerChange = (retailer?: { id: string } | null) => {
    setValue('retailerId', retailer?.id || '');
  };

  const handleCurrencyChange = (currency?: { id: string } | null) => {
    setValue('currencyCode', currency?.id || '');
  };

  const handleStatusChange = (checked: boolean) => {
    setValue('status', checked ? 'ACTIVE' : 'INACTIVE');
  };

  const handleShowVatChange = (checked: boolean) => {
    setValue('showVat', checked);
  };

  const handleOrderPredictionEnabled = (checked: boolean) => {
    setValue('orderPrediction.enabled', checked);
  };

  const handleOrderPredictionAlgorithm = (algorithm?: OrderPredictionAlgorithm | null) => {
    setValue('orderPrediction.algorithm', algorithm || defaultAlgorithm);
  };

  const onSubmitForm = async (data: StoreCreate | Store) => {
    setLoading(true);
    try {
      await onSubmit(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <div className="grid grid-cols-2 gap-6 lg:gap-8">
          {mode !== 'address' && (
            <div className={`col-span-2 ${sectionClassName}`}>
              <div className="mb-6 flex items-end justify-between gap-4 border-b border-slate-200 pb-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Store details</h2>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div>
                  <label htmlFor="name" className="text-sm font-medium text-slate-700">
                    Name
                  </label>
                  <input
                    id="name"
                    {...register('name', { required: 'Name is required' })}
                    className={inputClassName}
                  />
                  {errors.name && (
                    <span className="mt-2 block text-sm text-red-500">{errors.name.message}</span>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="text-sm font-medium text-slate-700">
                    Email
                  </label>
                  <input id="email" {...register('email')} className={inputClassName} />
                </div>

                <div className="lg:col-span-2">
                  <label htmlFor="description" className="text-sm font-medium text-slate-700">
                    Description
                  </label>
                  <textarea
                    id="description"
                    {...register('description')}
                    className={textAreaClassName}
                  />
                </div>

                <div>
                  <Controller
                    name="retailerId"
                    control={control}
                    render={({ field }) => (
                      <RetailerSelectBox
                        value={field.value}
                        onSelect={handleRetailerChange}
                        requireSelection
                        showOrganization
                      />
                    )}
                  />
                </div>

                <div>
                  <Controller
                    name="currencyCode"
                    control={control}
                    render={({ field }) => (
                      <CurrencySelectBox
                        value={field.value}
                        onSelect={handleCurrencyChange}
                        requireSelection
                      />
                    )}
                  />
                </div>

                <div>
                  <label htmlFor="contactNumber" className="text-sm font-medium text-slate-700">
                    Contact Number
                  </label>
                  <div className="mt-2">
                    <PhoneNumberInput
                      onChange={(countryCode) => setValue('contactCountryCode', countryCode)}
                      id="contactNumber"
                      countryCode={contactCountryCode}
                      inputProps={{ ...register('contactNumber') }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {mode === 'full' && (
            <div className="col-span-2 space-y-6">
              {businessAddressIndex >= 0 && (
                <div className={sectionClassName} data-testid="store-address-section-business">
                  <div className="mb-6 border-b border-slate-200 pb-4">
                    <span className="block text-lg font-semibold text-slate-900">
                      {getAddressLabel(StoreAddressType.BUSINESS)}
                    </span>
                  </div>
                  <StoreAddressForm name={`addresses.${businessAddressIndex}`} />
                </div>
              )}

              {deliveryAddressIndex >= 0 && (
                <div className={sectionClassName} data-testid="store-address-section-delivery">
                  <div className="mb-6 border-b border-slate-200 pb-4">
                    <span className="block text-lg font-semibold text-slate-900">
                      {getAddressLabel(StoreAddressType.DELIVERY)}
                    </span>
                  </div>
                  <StoreAddressForm name={`addresses.${deliveryAddressIndex}`} />
                </div>
              )}
            </div>
          )}

          {mode === 'address' && (
            <div className="col-span-2">
              {activeAddressIndex >= 0 && (
                <div
                  className={sectionClassName}
                  data-testid={
                    activeAddressType === StoreAddressType.BUSINESS
                      ? 'store-address-section-business'
                      : 'store-address-section-delivery'
                  }
                >
                  <div className="mb-6 border-b border-slate-200 pb-4">
                    <span className="block text-lg font-semibold text-slate-900">
                      {getAddressLabel(activeAddressType)}
                    </span>
                  </div>
                  <StoreAddressForm name={`addresses.${activeAddressIndex}`} />
                </div>
              )}
            </div>
          )}

          {mode !== 'address' && (
            <div className={`col-span-2 ${sectionClassName}`}>
              <div className="mb-6 border-b border-slate-200 pb-4">
                <h2 className="text-lg font-semibold text-slate-900">Operational settings</h2>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <ToggleSwitch
                    checked={showVat}
                    onChange={handleShowVatChange}
                    labelText="Show VAT:"
                  />
                  <span className="mt-3 block text-sm text-slate-500">
                    Include VAT in displayed store prices.
                  </span>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <ToggleSwitch
                    checked={orderPredictionEnabled}
                    onChange={handleOrderPredictionEnabled}
                    labelText="Auto Order:"
                  />

                  <div className="mt-4">
                    <OrderPredictionAlgorithmSelectBox
                      value={orderPredictionAlgorithm || defaultAlgorithm}
                      onSelect={(item) => handleOrderPredictionAlgorithm(item?.id)}
                      label="Algorithm:"
                      placeholder="Select algorithm"
                      disabled={!orderPredictionEnabled}
                      requireSelection
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <ToggleSwitch
                    checked={isStatusActive({ status })}
                    onChange={handleStatusChange}
                    labelText="Status:"
                  />
                  <span className="mt-3 block text-sm text-slate-500">
                    Toggle to make the store active or inactive.
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 flex justify-end">
          <Button
            type="submit"
            variant="primary"
            icon={<CheckIcon className="h-6 w-6" aria-hidden="true" />}
            loadingIconClassName="h-5 w-5 mr-1"
            loading={loading}
          >
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default StoreForm;
